/**
 * Created by rathompson on 10/9/23
 * File: FederationAuditLogsTable.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, VFC } from 'react'
import V5TableWrapper from '../../../../../common/components/table/V5TableWrapper'
import {
  ConnectedTable,
  TableProvider,
  TableInstance,
  downloadCSV,
  useTableInstance, ConnectedResultsSummary, ConnectedPaginator
} from '@panwds/react-table'
import type { IAM_Federation } from '../../../../../common/reducers/types/IAMReducer.types'
import { Body, useIsMounted } from '@panwds/react-ui'
import { FileImportIcon } from '@panwds/icons'
import TooltipButton from '../../../../../common/components/tooltipButton/TooltipButton'
import { GetAuditLogs } from '../../../../actions/IAMActions'
import GlobalContext from '../../../../../common/context/GlobalContext'
import { DateRangeFilter, FilterBar, SelectFilter, useFilters } from '@panwds/react-filters'
import { useMessage } from '../../../../../common/providers/MessageProvider'
import _ from 'lodash'
import moment from 'moment'
import { ARRAY, OBJECT } from '../../../../../common/utils/Utils'
import styled from 'styled-components'

const CONTAINER = styled(V5TableWrapper)`
  flex-grow: 1;
  height: 0;
  [role="table"] {
    overflow: scroll;
  }
  display: flex;
  flex-direction: column;
  [data-testid="connected-table-layout"] {
    & > div {
      flex-grow: 1;
    }
  }
  div[role="row"] {
    width: auto !important;
  }
`

const dateFilterFn: (range: { start: string | number, end: string | number }, items: any[]) => any = ({ start, end }, items) => {
  return items.filter(item => moment(item.timestamp).isBetween(start, end))
}

const DownloadButton: VFC<{ fileName: (() => string) | string }> = ({ fileName }) => {

  const tableInstance = useTableInstance()
  const disabled = tableInstance.rows.length === 0

  return (
    <TooltipButton
      label="Download the current table data"
      aria-label="Download"
      disabled={disabled}
      buttonProps={{
        appearance: 'secondary',
        icon: <FileImportIcon size="md"/>,
        onClick: () => {
          const fn = typeof fileName === 'function' ? fileName() : fileName
          downloadCSV(tableInstance, fn)
        }
      }}
    >Export</TooltipButton>
  )
}

const FederationAuditLogsTable: VFC<{
  federation: IAM_Federation
}> = ({ federation }) => {

  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer

  const mounted = useIsMounted()
  const [loading, setLoading] = useState(false)
  const [logs, setLogs] = useState([])
  const [resolvedData, setResolvedData] = useState([])
  const { filters } = useFilters('federationAuditLogFilters')
  const { apiBannerHandler } = useMessage()

  const domain = federation.domain

  const loadAuditLogs = useCallback((domain) => {
    setLoading(true)
    GetAuditLogs(selectedTreeNode?.id, domain).then(logs => {
      if (mounted.current) {
        setLogs(logs)
      }
    }).catch(apiBannerHandler).finally(() => setLoading(false))
  }, [apiBannerHandler, mounted, selectedTreeNode?.id])

  useEffect(() => {
    loadAuditLogs(domain)
  }, [domain, loadAuditLogs])

  useEffect(() => {
    const {
      date,
      owner,
      category,
      action,
      status,
      sub_category
    } = filters
    let filteredLogs = _.cloneDeep(logs)
    if (date) {
      if (['7', '30'].includes(date)) {
        const end = moment().endOf('day').valueOf()
        const start = moment().subtract(parseInt(date, 10), 'days').startOf('day').valueOf()
        filteredLogs = dateFilterFn({ start, end }, filteredLogs)
      }
      else if (OBJECT.hasObject(date)) {
        filteredLogs = dateFilterFn(date, filteredLogs)
      }
    }
    if (owner && ARRAY.hasArray(owner)) {
      filteredLogs = filteredLogs.filter((log: any) => owner.includes(log.user_id))
    }
    if (sub_category && ARRAY.hasArray(sub_category)) {
      filteredLogs = filteredLogs.filter((log: any) => sub_category.includes(log.sub_category))
    }
    if (category && ARRAY.hasArray(category)) {
      filteredLogs = filteredLogs.filter((log: any) => category.includes(log.category))
    }
    if (action && ARRAY.hasArray(action)) {
      filteredLogs = filteredLogs.filter((log: any) => action.includes(log.action))
    }
    if (status && ARRAY.hasArray(status)) {
      filteredLogs = filteredLogs.filter((log: any) => status.includes(log.status))
    }
    setResolvedData(filteredLogs as any)
  }, [filters, logs])

  const tableInstanceRef = useRef<TableInstance<any> | null>(null)

  const columns = useMemo(() => {
    return [
      {
        Header: 'User Name',
        id: 'user_id',
        accessor: 'user_id',
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{value}</Body>,
        width: 240
      },
      {
        Header: 'Category',
        id: 'category',
        accessor: 'category',
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{value}</Body>
      },
      {
        Header: 'Sub-Category',
        id: 'sub_category',
        accessor: 'sub_category',
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{value}</Body>
      },
      {
        Header: 'Action',
        id: 'action',
        accessor: 'action',
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{_.capitalize(value)}</Body>
      },
      {
        Header: 'Action Status',
        id: 'status',
        accessor: 'action_status',
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{_.capitalize(value)}</Body>
      },
      {
        Header: 'Details',
        id: 'description',
        accessor: 'details.text',
        width: 300,
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{value}</Body>
      },
      {
        Header: 'Timestamp',
        id: 'timestamp',
        accessor: 'timestamp',
        Cell: ({ value }: any) => <Body addClassName="cm-truncate">{moment(value).format('dddd, MMMM Do YYYY, h:mm:ss a')}</Body>,
        sticky: true,
        width: 300,
        stickyPosition: 'right' as 'right'
      }
    ]
  }, [])

  const filterConfig = useMemo(() => {
    return [
      {
        Component: DateRangeFilter,
        context: {
          items: [
            {
              value: 'all',
              children: 'All'
            },
            {
              value: '30',
              children: 'Past 30 Days'
            },
            {
              value: '7',
              children: 'Past 7 Days'
            }
          ]
        },
        label: 'Date Range',
        name: 'date',
        static: true,
        required: true,
        defaultValues: 'all'
      },
      {
        Component: SelectFilter,
        context: {
          enableMultiSelect: true,
          enableSelectAll: true,
          enableSearch: true,
          items: [...new Set(logs.map(({ user_id }) => user_id))].map((user_id: any) => ({
            children: user_id,
            value: user_id
          }))
        },
        label: 'User Name',
        name: 'owner',
        defaultValues: []
      },
      {
        Component: SelectFilter,
        context: {
          enableMultiSelect: true,
          enableSelectAll: true,
          items: [...new Set(logs.map(({ category }) => category))].map(category => ({
            children: _.capitalize(category),
            value: category
          }))
        },
        label: 'Category',
        name: 'category',
        defaultValues: [...new Set(logs.map(({ category }) => category))]
      },
      {
        Component: SelectFilter,
        context: {
          enableMultiSelect: true,
          enableSelectAll: true,
          items: [...new Set(logs.map(({ sub_category }) => sub_category))].map(sub_category => ({
            children: _.capitalize(sub_category),
            value: sub_category
          }))
        },
        label: 'Sub-Category',
        name: 'sub_category',
        defaultValues: [...new Set(logs.map(({ sub_category }) => sub_category))]
      },
      {
        Component: SelectFilter,
        context: {
          enableMultiSelect: true,
          enableSelectAll: true,
          items: [...new Set(logs.map(({ action }) => action))].map(action => ({
            children: _.capitalize(action),
            value: action
          }))
        },
        label: 'Action',
        name: 'action',
        defaultValues: [...new Set(logs.map(({ action }) => action))]
      },
      {
        Component: SelectFilter,
        context: {
          enableMultiSelect: true,
          enableSelectAll: true,
          items: [...new Set(logs.map(({ action_status }) => action_status))].map(action_status => ({
            children: _.capitalize(action_status),
            value: action_status
          }))
        },
        label: 'Action Status',
        name: 'status',
        defaultValues: [...new Set(logs.map(({ action_status }) => action_status))]
      }
    ]
  }, [logs])

  const fileName = `${domain}.csv`

  const LeftControls = () => {
    return (
      <div className="cm-flex cm-gap-2">
        <FilterBar
          filters={filterConfig}
          name="federationAuditLogFilters"
          showResetBtn={false}
        />
      </div>
    )
  }

  const RightControls = () => {
    return (
      <DownloadButton {...{ fileName }}/>
    )
  }

  return (
    <CONTAINER className="cm-flex-grow cm-border cm-border-solid cm-border-gray-300 cm-border-b-0 cm-border-r-0 cm-border-l-0">
      <TableProvider
        tableInstanceRef={tableInstanceRef}
        columns={columns}
        data={resolvedData}
        isLoading={loading}
        enablePagination
        enableColumnOrder
        enableResizeColumns
        enableColumnSort
      >
        <div className="cm-flex cm-justify-between cm-p-2">
          <LeftControls/>
          <RightControls/>
        </div>
        <div className='cm-flex cm-flex-col tw-flex-grow tw-overflow-auto'>
          <ConnectedTable/>
        </div>
        <div className='tw-flex tw-justify-between tw-items-center tw-p-1'>
          <ConnectedResultsSummary/>
          <ConnectedPaginator/>
        </div>
      </TableProvider>
    </CONTAINER>
  )
}

export default FederationAuditLogsTable
